'use client';

import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MissionPromoApi } from '@/api/cms/missionPromo';
import { IconComponent } from '@/components/IconComponent';
import { getCDNImageUrl } from '@/helpers/cdn';
import { getTimeLeft } from '@/helpers/timer';
import { useTimeoutRegistrationModal } from '@/hooks/timeoutRegistrationModal';
import { HorizontalCountdown } from '@/modules/common/components/HorizontalCountdown';
import { ReceiveBonusesNotice } from '@/modules/common/components/ReceiveBonusesNotice';
import { useCMSConfig } from '@/modules/common/hooks/useCMSConfig';
import { FAQByGroup } from '@/modules/restrictions/components/FAQByGroup';
import { StepsByGroup } from '@/modules/restrictions/components/StepsByGroup';
import { useGetCommonStore } from '@/store';
import { EMissionBonusGroupKey } from '../../helpers';
import { useMissionScroll } from '../../hooks/useMissionScroll';
import { useMissions } from '../../hooks/useMissions';
import { ButtonCTA } from '../ButtonCTA';
import { Missions } from '../Missions';
import { ScMissionPageContainer } from './styled';

interface IMissionPageProps {
  data: MissionPromoApi;
}

export const MissionPage: FC<IMissionPageProps> = ({ data }) => {
  const { t } = useTranslation('mission');

  useTimeoutRegistrationModal();

  const { currentMission } = useMissions();
  const useCommonStore = useGetCommonStore();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const user = useCommonStore.use.user();
  const lootboxes = useCommonStore.use.withoutTagsLootboxes();

  const config = useCMSConfig();
  const timeLeft = getTimeLeft(config.data?.mission?.end || null);

  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
  } = useMissionScroll({
    currentMissionCharacter: currentMission.character,
    isMobileDevice,
    hasUserData: !!user,
  });

  const isPromoFinished = useMemo(() => {
    if (!user) return false;
    return lootboxes.find(
      ({ groupKey }) => groupKey === EMissionBonusGroupKey.L5
    );
  }, [user, lootboxes]);

  return (
    <ScMissionPageContainer>
      <div className="section-1">
        <video
          autoPlay
          className="bg-1"
          loop
          muted
          playsInline
          preload="metadata"
          src={getCDNImageUrl('/mission/bg-1.webm')}
        />
        <div className="bonuses-notice-box">
          <ReceiveBonusesNotice
            successMessage="canIssueBonuses.success"
            translation="promotions"
            warningMessage="canIssueBonuses.warning"
          />
        </div>
        <h1 className="title">{t('title')}</h1>
        <p className="description">{t('description')}</p>
        <HorizontalCountdown className="countdown" timeLeft={timeLeft} />
        <div className="mission-stages">
          <button
            className={`mission-stages-button left ${canScrollLeft ? 'active' : 'disabled'}`}
            disabled={!canScrollLeft}
            onClick={handleScrollLeft}
            type="button"
          >
            <IconComponent name="whiteArrowRight" />
          </button>
          <button
            className={`mission-stages-button right ${canScrollRight ? 'active' : 'disabled'}`}
            disabled={!canScrollRight}
            onClick={handleScrollRight}
            type="button"
          >
            <IconComponent name="whiteArrowRight" />
          </button>
          <Missions ref={scrollContainerRef} />
          <p className="mission-description">
            {isPromoFinished
              ? t('promo-finished-decription')
              : t(currentMission.descriptionKey)}
          </p>
          <ButtonCTA />
        </div>
      </div>
      <div className="section-2">
        <StepsByGroup
          imgSize={{ width: 84, height: 84 }}
          sections={data.steps}
        />
        <FAQByGroup sections={data.faqs} withTitle />
      </div>
    </ScMissionPageContainer>
  );
};
